/**
 * AI Tier Indicator Component
 * Shows user whether they have Standard or Premium Therappi AI
 */

import { useEffect, useState } from 'react';
import { Sparkles, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export const AITierIndicator = ({ variant = 'badge' }: { variant?: 'badge' | 'card' }) => {
  const [user, setUser] = useState<any>(null);
  
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data.user));
  }, []);
  
  const { isPremium, isAdmin, isLoading } = useUserRole(user);
  
  const hasPremiumAI = isAdmin || isPremium;

  if (isLoading) return null;

  if (variant === 'badge') {
    return (
      <Badge 
        variant={hasPremiumAI ? 'default' : 'secondary'}
        className="gap-1"
      >
        {hasPremiumAI ? (
          <>
            <Sparkles className="h-3 w-3" />
            Premium AI
          </>
        ) : (
          <>
            <Zap className="h-3 w-3" />
            Standard AI
          </>
        )}
      </Badge>
    );
  }

  if (hasPremiumAI) {
    return (
      <Card className="relative border-0 bg-gradient-to-br from-primary/10 via-accent/5 to-card/50 backdrop-blur-sm shadow-md overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-radial from-primary/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-light text-foreground tracking-wide">Premium Therappi® AI</h3>
              <p className="text-xs text-muted-foreground font-light">Active</p>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground font-light leading-relaxed">
            Deep timeline integration, cross-domain pattern analysis, and predictive insights across all your data - physical, emotional, mental, relational, and spiritual.
          </p>

          <div className="pt-2 space-y-2 text-xs text-muted-foreground font-light">
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Full Past → Present → Future timeline reasoning</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Cross-domain correlations (biometrics ↔ mood ↔ cycle ↔ nutrition)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Predictive insights and proactive pattern alerts</span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Standard AI - Show upgrade prompt
  return (
    <Card className="relative border-0 bg-gradient-to-br from-card/80 via-primary/5 to-accent/5 backdrop-blur-sm shadow-lg overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary via-accent to-secondary" />
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-radial from-primary/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <div className="p-6 sm:p-8 pl-8 space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-light text-foreground tracking-wide">Unlock Premium Therappi® AI</h3>
            <p className="text-sm text-muted-foreground font-light leading-relaxed">
              Multidimensional intelligence connecting your past, present and future across every level of being - physical, emotional, mental, relational, and spiritual.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-sm font-light text-foreground/80">Premium AI integrates everything you track:</p>
          
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>WHOOP biometric intelligence</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>Hormonal cycle insights</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>Food-mood pattern analysis</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>Sanctuary nervous system tracking</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>Integrated couples facilitation</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>Deep timeline reasoning</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>Future Self alignment</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-light">
              <span className="text-primary">✓</span>
              <span>Predictive pattern alerts</span>
            </div>
          </div>

          <p className="text-sm italic text-primary/80 font-light pt-2">
            Every module you subscribe to feeds your AI - the more you track, the deeper Therappi® understands you.
          </p>
        </div>

        <UpgradeButton />
      </div>
    </Card>
  );
};

const UpgradeButton = () => {
  const navigate = useNavigate();
  
  return (
    <Button 
      onClick={() => navigate('/subscription')}
      className="w-full group font-light tracking-wide"
      size="lg"
    >
      <Sparkles className="h-4 w-4 mr-2" />
      Upgrade to Premium AI
    </Button>
  );
};
